import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  OrganizationSeason,
  OrganizationSeasonCreateRequest,
  OrganizationSeasonUpdateRequest
} from '../models/sponsor.model';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Organization Seasons Service (Admin)
 *
 * Service for managing per-organization agricultural spraying seasons.
 * Communicates with /admin/organizations/:orgId/seasons endpoints.
 *
 * Reference: /agent-os/specs/2026-02-11-acres-advantage/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class OrganizationSeasonsService {
  constructor(private http: HttpClient) {}

  /**
   * Build the API URL for a given organization
   */
  private getApiUrl(organizationId: string): string {
    return `${environment.apiUrl}/admin/organizations/${organizationId}/seasons`;
  }

  /**
   * List all seasons for an organization
   */
  listSeasons(organizationId: string): Observable<OrganizationSeason[]> {
    return this.http.get<ApiResponse<{ seasons: OrganizationSeason[] }>>(this.getApiUrl(organizationId)).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data.seasons;
        }
        return [];
      })
    );
  }

  /**
   * Create a new season for an organization
   */
  createSeason(organizationId: string, data: OrganizationSeasonCreateRequest): Observable<OrganizationSeason> {
    return this.http.post<ApiResponse<OrganizationSeason>>(this.getApiUrl(organizationId), data).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to create season');
      })
    );
  }

  /**
   * Update an existing season
   */
  updateSeason(organizationId: string, seasonId: string, data: OrganizationSeasonUpdateRequest): Observable<OrganizationSeason> {
    return this.http.put<ApiResponse<OrganizationSeason>>(`${this.getApiUrl(organizationId)}/${seasonId}`, data).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to update season');
      })
    );
  }
}
